import { useState } from 'react';
import { Button, Card, Typography } from 'antd';

import { useAppDispatch } from '@/core/redux/hooks';
import { subscriptionActions } from '@/core/redux/slices/subscription/slice';

const { Text, Title } = Typography;

const subscriptionTypes: any = {
  professional: {
    title: (
      <div className='pb-3'>
        <Title level={4}>Профессионал</Title>
        <Text type='secondary' className='text-wrap'>
          Для индивидуальных инженеров-проектировщиков
        </Text>
      </div>
    ),
    button_title: 'Выбрать тариф',
    options: [
      <span>
        Полный комплекс из{' '}
        <span className='font-bold'>14+ инженерных расчетов</span>. Все
        необходимые инструменты для работы над проектом в одном сервисе.
      </span>,
      'Готовые отчеты по ГОСТ. Автоматическое формирование документации в открытом формате .docx.',
      'BIM-плагин для Renga. Интеграция расчетов аэродинамики и воздухообмена.',
      'База знаний и Справочник воздухообмена. Быстрый доступ к данным и нормам.',
      'Экспертная поддержка и помощь в чате по сложным вопросам вашего проекта.',
      'Приоритетный подбор оборудования и готовое технико-коммерческое предложение в течение 24 часов.',
      'Прямая экономия. Скидка 35% на противодымное оборудование от партнеров-производителей сервиса.',
    ],
  },
  team: {
    title: (
      <div className='flex flex-col pb-3'>
        <Title level={4}>Команда</Title>
        <Text type='secondary' className='text-wrap'>
          Для проектных отделов и организаций
        </Text>
        <Text type='secondary' className='text-wrap'>
          (от 3-х пользователей)
        </Text>
      </div>
    ),
    button_title: 'Обсудить условия',
    options: [
      'Все возможности тарифа «Профессионал».',
      'Непрерывность работы. Доступ к расчетам коллег для проверки или на время их отсутствия (отпуск, болезнь).',
      'Единая база проектов. Общий доступ к расчетам для исключения потерь данных и ускорения командной работы.',
      'Брендирование вашей компании в отчетах.',
      'Готовая таблица ХОВС (Excel) для вашего проекта (по запросу).',
    ],
  },
  corp: {
    title: (
      <div className='pb-3'>
        <Title level={4}>Корпоративный</Title>
        <Text type='secondary' className='text-wrap'>
          Для крупных организаций с особыми требованиями
        </Text>
      </div>
    ),
    button_title: 'Обсудить условия',
    options: [
      'Все возможности тарифа «Команда».',
      'Масштабирование на всю компанию. ',
      'Неограниченное количество пользователей.',
      'Стратегическое партнерство. Возможность влиять на развитие продукта и заказывать доработки под ваши уникальные задачи.',
      'Организованное внедрение и консультации по интеграции. Командный обучающий онлайн-семинар.',
    ],
  },
};

const periodEnums: any = {
  1: '1 месяц',
  3: '3 месяца',
  6: '6 месяцев',
  12: '1 год',
};

interface IPriceData {
  period: number;
  price: string;
}

export const CardComponent: React.FC<any> = ({ subscription }) => {
  const [activeTabKey, setActiveTabKey] = useState<string>(
    subscription?.pricing[0]?.period || '0'
  );

  const dispatch = useAppDispatch();

  const tabListOptions = subscription?.pricing?.map((priceData: IPriceData) => {
    return {
      key: priceData?.period,
      label: periodEnums?.[priceData?.period],
    };
  });

  const buy_subscription = ({ period, price }: any) => {
    dispatch(
      subscriptionActions.fetchBuySubscription({
        type: subscription?.type,
        period,
        price,
      })
    );
  };

  const onTabChange = (key: string) => {
    setActiveTabKey(key);
  };

  const getPrice = () => {
    const data = subscription?.pricing?.find(
      (price: IPriceData) => +price?.period === +activeTabKey
    );

    const convert_price = (value: number) => {
      return value.toLocaleString('ru-RU', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
    };

    if (subscription?.type === 'corp')
      return (
        <div className='flex justify-between items-center'>
          <div className='flex gap-5 items-end'>
            <Text className='font-semibold' style={{ fontSize: '24px' }}>
              По запросу
            </Text>
          </div>
          <Button
            type='primary'
            onClick={() =>
              buy_subscription({ period: data?.period, price: data?.price })
            }
          >
            {subscriptionTypes?.[subscription?.type]?.button_title}
          </Button>
        </div>
      );

    return (
      <div className='flex justify-between items-center'>
        <div className='flex gap-5 items-end'>
          <Text className='font-semibold' style={{ fontSize: '24px' }}>
            {convert_price(+data?.price)} ₽
          </Text>
          <Text className='mb-1' type='secondary' style={{ fontSize: '14px' }}>
            {subscription?.type === 'team'
              ? data?.price
              : convert_price(data?.price / data?.period)}{' '}
            ₽/ {subscription?.type === 'team' ? 'пользователь в месяц' : 'мес.'}
          </Text>
        </div>
        <Button
          type='primary'
          onClick={() =>
            buy_subscription({ period: data?.period, price: data?.price })
          }
        >
          {subscriptionTypes?.[subscription?.type]?.button_title}
        </Button>
      </div>
    );
  };

  return (
    <Card
      title={subscriptionTypes?.[subscription?.type]?.title}
      tabList={tabListOptions}
      tabProps={{
        size: 'middle',
      }}
      activeTabKey={activeTabKey}
      onTabChange={onTabChange}
    >
      <div className='flex flex-col gap-4'>
        {getPrice()}
        <div>
          <Title level={5}>Ключевые возможности:</Title>
          {subscriptionTypes?.[subscription?.type]?.options?.map(
            (option: string | React.ReactNode, ind: number) => (
              <div key={ind} className='flex gap-2'>
                <span>•</span> <span>{option}</span>
              </div>
            )
          )}
        </div>
      </div>
    </Card>
  );
};
